import { BrowserRouter as Router, Route, Routes, useParams, useNavigate, Navigate, useLocation } from 'react-router-dom';
import React, { useEffect } from 'react';

import Home from './pages/Home.jsx'
import About from './pages/About.jsx'
import OurCommittee from './pages/OurCommittee.jsx'
import ApplyOnline from './pages/ApplyOnline.jsx';
import Contact from './pages/Contact.jsx';
import Reporting from './pages/Reporting.jsx';
import Research from './pages/Research.jsx';
import FreedomOfPress from './pages/FreedomOfPress.jsx';
import IndependenceOfReporting from './pages/IndependenceOfReporting.jsx';
import CodeofEthics from './pages/CodeOfEthics.jsx';
import CriteriaAndRules from './pages/CriteriaAndRules.jsx';
import FAQ from './pages/FAQ.jsx';
import PressMedia from './pages/Forms/PressMedia.jsx';
import Broadcasting from './pages/Forms/Broadcasting.jsx';
import DigitalMedia from './pages/Forms/DigitalMedia.jsx';
import CitizenReporter from './pages/Forms/CitizenReporter.jsx';
import Login from './pages/Login.jsx';
import Register from './pages/Register.jsx';
import ResetPassword from './pages/ResetPassword.jsx';
import Dashboard from './pages/Dashboard.jsx';
import ReportNews from './pages/ReportNews.jsx';
import NaiNews from './pages/NaiNews.jsx';
import NaiNewsDetail from './pages/NaiNewsDetail.jsx';
import Events from './pages/Events.jsx';
import Videos from './pages/Videos.jsx';
import Photos from './pages/Photos.jsx';
import MediaState from './pages/MediaState.jsx';
import JournalistProblems from './pages/JournalistProblems.jsx';
import Challenges from './pages/Challenges.jsx';
import MemberProfile from './pages/MemberProfile.jsx';
import PrivacyPolicy from './pages/PrivacyPolicy.jsx';
import TermsOfService from './pages/TermsOfService.jsx';
import Disclaimer from './pages/Disclaimer.jsx';
import Header from './components/Header.jsx'
import Footer from './components/Footer.jsx'
import { BACKEND_URL } from './config/api';
import { FaWhatsapp } from 'react-icons/fa';
import ScrollToTop from './components/ScrollToTop.jsx';
import ErrorBoundary from './components/ErrorBoundary.jsx';
import { trackPageView, sendHeartbeat } from './utils/analyticsTracker';

const getAdminUrl = (path = '') => {
  const hostname = window.location.hostname;
  const port = window.location.port;
  if (hostname === 'localhost' || hostname === '127.0.0.1' || hostname === 'admin.localhost') {
    return `http://admin.localhost${port ? `:${port}` : ''}${path}`;
  }
  const baseDomain = hostname.replace(/^admin\./, '').replace(/^www\./, '');
  return `${window.location.protocol}//admin.${baseDomain}${path}`;
};

const getRootUrl = (path = '') => {
  const hostname = window.location.hostname;
  const port = window.location.port;
  if (hostname === 'localhost' || hostname === '127.0.0.1' || hostname === 'admin.localhost') {
    return `http://localhost${port ? `:${port}` : ''}${path}`;
  }
  const baseDomain = hostname.replace(/^admin\./, '');
  return `${window.location.protocol}//${baseDomain}${path}`;
};

function PublicRedirectHandler() {
  const location = useLocation();
  useEffect(() => {
    const adminPaths = ['/login', '/register', '/dashboard', '/reset-password'];
    const isAdminPath = adminPaths.some(path => location.pathname.startsWith(path));
    if (isAdminPath) {
      window.location.href = getAdminUrl(location.pathname + location.search);
    }
  }, [location]);
  return null;
}

function AdminRedirectHandler() {
  const location = useLocation();
  useEffect(() => {
    const adminPaths = ['/login', '/register', '/dashboard', '/reset-password', '/admin'];
    const isAdminPath = adminPaths.some(path => location.pathname.startsWith(path));
    
    if (location.pathname === '/') {
      window.location.replace('/login');
    } else if (!isAdminPath) {
      window.location.href = getRootUrl(location.pathname + location.search);
    }
  }, [location]);
  return null;
}

function AdminRedirect() {
  useEffect(() => {
    // Redirect to the Django Admin panel served by the backend
    window.location.href = `${BACKEND_URL}/portal-manage-login/`;
  }, []);
  return (
    <div className="text-center py-20 bg-gray-50 min-h-screen flex flex-col items-center justify-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
      <p className="text-gray-600 text-lg font-medium">Redirecting to Django Admin...</p>
    </div>
  );
}



function Layout({ children }) {
  const location = useLocation();

  useEffect(() => {
    // Track visitor page view
    trackPageView(location.pathname + location.search, document.title);

    // Heartbeat ping every 2 minutes while active
    const interval = setInterval(() => {
      sendHeartbeat();
    }, 120000);

    return () => clearInterval(interval);
  }, [location]);

  return (
    <>
      <ScrollToTop />
      <Header />
      {children}
      <Footer />
      
      {/* Floating WhatsApp Button */}
      <a
        href="https://wa.me/917200024453?text=Hi,%20I%20saw%20your%20organization%20on%20your%20website.%20How%20can%20I%20join?"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 z-50 bg-green-500 text-white p-4 rounded-full shadow-lg hover:bg-green-600 transition-colors duration-300 flex items-center justify-center animate-bounce"
        aria-label="Chat on WhatsApp"
        style={{ zIndex: 9999 }}
      >
        <FaWhatsapp className="w-8 h-8" />
      </a>
    </>
  );
}

function App() {
  const isAdminHost = window.location.hostname === 'admin.localhost' || window.location.hostname.startsWith('admin.');

  if (isAdminHost) {
    return (
      <ErrorBoundary>
        <Router>
          <AdminRedirectHandler />
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/reset-password/:uid/:token" element={<ResetPassword />} />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/admin" element={<AdminRedirect />} />
            <Route path="*" element={<Navigate to="/login" replace />} />
          </Routes>
        </Router>
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary>
      <Router>
        <PublicRedirectHandler />
        <Routes>
          <Route path="/*" element={
            <Layout>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/about" element={<About />} />
                <Route path="/committee" element={<OurCommittee />} />
                <Route path="/apply" element={<ApplyOnline />} />
                <Route path="/apply/press-media" element={<PressMedia />} />
                <Route path="/apply/broadcasting" element={<Broadcasting />} />
                <Route path="/apply/digital-media" element={<DigitalMedia />} />
                <Route path="/apply/citizen-reporter" element={<CitizenReporter />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/reporting" element={<Reporting />} />
                <Route path="/research" element={<Research />} />
                <Route path="/freedom-of-press" element={<FreedomOfPress />} />
                <Route path="/independence-of-reporting" element={<IndependenceOfReporting />} />
                <Route path="/code-of-ethics" element={<CodeofEthics />} />
                <Route path="/criteria-rules" element={<CriteriaAndRules />} />
                <Route path="/faq" element={<FAQ />} />
                <Route path="/videos" element={<Videos />} />
                <Route path="/photos" element={<Photos />} />
                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                <Route path="/terms-of-service" element={<TermsOfService />} />
                <Route path="/disclaimer" element={<Disclaimer />} />
                
                <Route path="/report-news" element={<ReportNews />} />
                <Route path="/nai-news" element={<NaiNews />} />
                <Route path="/nai-news/:id" element={<NaiNewsDetail />} />
                <Route path="/events" element={<Events />} />
                <Route path="/media-state" element={<MediaState />} />
                <Route path="/journalist-problems" element={<JournalistProblems />} />
                <Route path="/reporter-challenges" element={<Challenges />} />
                {/* Catch-all route for member profiles (flexible slugs) */}
                <Route path="*" element={<MemberProfile />} />
              </Routes>
            </Layout>
          } />
        </Routes>
      </Router>
    </ErrorBoundary>
  );
}

export default App;